import { IndicatorStore } from '../stores/indicator/indicator.state';
import { ActiveUserStore } from '../stores/user/active-user.state';
import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { IndicatorDataStore } from '../stores/indicatpr-data/indicator-data.state';
import { DataLayerStore } from '../stores/data-layer/data-layer.state';
import { DataLayerTypeStore } from '../stores/data-layer-type/data-layer-type.state';
import { CategoryStore } from '../stores/category/category.state';
import { GlobalsStore } from '../stores/globals.state';
import { GeoDataStore } from '../stores/geo/geo-data.state';
import { BookmarkCategoryStore } from '../stores/bookmark-category/bookmark-category.state';
import { BookmarkStore } from '../stores/bookmark/bookmark.state';
import { RoleStore } from '../stores/role/role.state';
import { PermissionStore } from '../stores/permission.state';
import { getState } from '@ngrx/signals';
import { Commune } from '../models/commune';
import { RoutingService } from '../services/routing.service';
import { StoryStore } from '../stores/story/story.state';
import { NotificationStore } from '../stores/notification/notification.state';
import { ColorsetStore } from '../stores/colorset/colorset.state';

@Injectable({
  providedIn: 'root',
})
export abstract class InitializationHandler {
  readonly activeUserStore = inject(ActiveUserStore);
  readonly layerTypeStore = inject(DataLayerTypeStore);
  readonly layerStore = inject(DataLayerStore);
  readonly indicatorStore = inject(IndicatorStore);
  readonly categoryStore = inject(CategoryStore);
  readonly indicatorDataStore = inject(IndicatorDataStore);
  readonly globalStore = inject(GlobalsStore);
  readonly geoDataStore = inject(GeoDataStore);
  readonly userStore = inject(ActiveUserStore);
  readonly bookmarkCategoryStore = inject(BookmarkCategoryStore);
  readonly bookmarkStore = inject(BookmarkStore);
  readonly storyStore = inject(StoryStore);
  readonly roleStore = inject(RoleStore);
  readonly permissionStore = inject(PermissionStore);
  readonly notificationStore = inject(NotificationStore);
  readonly colorsetStore = inject(ColorsetStore);

  constructor(protected routingService: RoutingService) {
    // Once categories are initialized, fetch indicators
    effect(() => {
      if (this.areCategoriesInitialized()) {
        queueMicrotask(() => this.handleIndicators());
      }
    });

    // Once layer-types are initialized, fetch layers
    effect(() => {
      if (this.areLayerTypesInitialized()) {
        queueMicrotask(() => this.handleLayers());
      }
    });

    // Once indicators are initialized, fetch indicator data
    effect(() => {
      if (this.areCategoriesInitialized() && this.areLayersInitialized()) {
        queueMicrotask(() => this.handleIndicatorData());
      }
    });

    // Once bookmarkCategories are initialized, fetch bookmarks data
    effect(() => {
      if (this.areBookmarkCategoriesInitialized()) {
        queueMicrotask(() => this.handleBookmarks());
      }
    });
  }

  // Shared state variables
  protected _storesInitialized = signal(false);
  protected _activeCommune = signal<Commune | null>(null);

  readonly areLayerTypesInitialized = computed(
    () => getState(this.layerTypeStore).initialized
  );

  readonly areLayersInitialized = computed(
    () => getState(this.layerStore).initialized
  );

  readonly areCategoriesInitialized = computed(
    () => getState(this.categoryStore).initialized
  );

  readonly areBookmarkCategoriesInitialized = computed(
    () => getState(this.bookmarkCategoryStore).initialized
  );

  readonly areIndicatorsInitialized = computed(
    () =>
      getState(this.indicatorStore).initialized &&
      this.areCategoriesInitialized() // Depends on categories
  );

  readonly isIndicatorDataReady = computed(
    () =>
      getState(this.indicatorDataStore).initialized &&
      this.areIndicatorsInitialized() // Depends on indicators
  );

  /**
   * Checks if the stores have been initialized.
   *
   * @return {boolean} Returns true if the stores are initialized, otherwise false.
   */
  get storesInitialized(): boolean {
    return this._storesInitialized();
  }

  /**
   * Retrieves the active commune.
   *
   * @return {Commune | null} The currently active commune if available, otherwise null.
   */
  get activeCommune(): Commune | null {
    return this._activeCommune();
  }

  /**
   * Determines if all the necessary stores in the application have been initialized.
   *
   * @return {boolean} True if all required stores are initialized and the global store has no selected type, otherwise false.
   */
  areStoresInitialized(): boolean {
    const activeUserStore = getState(this.activeUserStore);
    const storyState = getState(this.storyStore);
    const colorsetState = getState(this.colorsetStore);
    const layerTypeState = getState(this.layerTypeStore);
    const layerState = getState(this.layerStore);
    const geoDataState = getState(this.geoDataStore);
    const indicatorState = getState(this.indicatorStore);
    const dataState = getState(this.indicatorDataStore);
    const categoryState = getState(this.categoryStore);
    const bookmarkCategoryState = getState(this.bookmarkCategoryStore);
    const bookmarkState = getState(this.bookmarkStore);
    const roleState = getState(this.roleStore);
    const notificationState = getState(this.notificationStore);

    return (
      layerTypeState.initialized &&
      layerState.initialized &&
      geoDataState.initialized &&
      indicatorState.initialized &&
      dataState.initialized &&
      categoryState.initialized &&
      bookmarkCategoryState.initialized &&
      bookmarkState.initialized &&
      roleState.initialized &&
      storyState.initialized &&
      colorsetState.initialized &&
      notificationState.initialized &&
      !!activeUserStore.commune // --> active commune is set by route-context.guard.ts
    );
  }

  /**
   * Handles the layers by determining the type IDs and fetching layers
   * associated with those type IDs for admin users. If the user is not an admin,
   * the method exits without performing any operations. For admin users, it
   * retrieves all type IDs from the layer type store and uses them to find
   * associated layers from the layer store.
   *
   * @return {void} This method does not return a value.
   */
  handleLayers(): void {
    if (this.activeUserStore.context() === 'dataBackend') return; // data gets loaded by layer-store
    const typeIds = this.layerTypeStore.allIds();

    this.layerStore.findByParentIds(typeIds);
  }

  /**
   * Handles the process of managing indicators based on user roles and category IDs.
   * Checks if the current user is an administrator and bypasses further execution if true.
   * Retrieves all category IDs from the category store and, if any are found, uses them to fetch corresponding indicators.
   *
   * @return {void} This method does not return a value.
   */
  handleIndicators(): void {
    if (
      this.activeUserStore.isAdmin() &&
      this.activeUserStore.context() === 'dataBackend'
    )
      return; // find all by store-initialization

    const categoryIds = this.categoryStore.allIds();
    if (categoryIds.length > 0) {
      this.indicatorStore.findByCategoryIds(categoryIds);
    }
  }

  /**
   * Handles the process of fetching and managing indicator data for the application.
   * If the active user is an admin, the method does nothing as data management is handled
   * by the indicator data store. Otherwise, it retrieves all data layer IDs and indicator
   * IDs from their respective stores and fetches the related data.
   *
   * @return {void} This method does not return any value.
   */
  handleIndicatorData(): void {
    if (this.activeUserStore.context() === 'dataBackend') return; // data gets loaded by indicator-data-store
    const dataLayerIds = this.layerStore.allIds();
    const categoryIds = this.categoryStore.allIds();
    this.indicatorDataStore.findMany({ dataLayerIds, categoryIds });
  }

  /**
   * Handles bookmark functionality by retrieving and processing bookmark categories and their associated bookmarks.
   * Only executes the operation if the active user has admin privileges.
   *
   * @return {void} No return value.
   */
  handleBookmarks(): void {
    if (
      !this.activeUserStore.isAdmin() &&
      this.activeUserStore.context() === 'dataBackend'
    )
      return; // data gets loaded by bookmark-store
    const categoryIds = this.bookmarkCategoryStore.allIds();

    this.bookmarkStore.findByCategoryIds(categoryIds);
    this.storyStore.findByCategoryIds(categoryIds);
  }

  /**
   * Abstract method to enforce custom initialization logic
   */
  abstract performInitialization(): void;
}
